/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Grid,
  Paper,
  Box,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import TextFieldSelectBox from '../../component/SelectBox';
import ScheduleActionControlContents from './ScheduleActionControlIconContents';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {getDailySchedule, putTodaySchedule} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {apiErrorAlert} from '../../functions/apiErrorAlert';
import {pullDownMenu} from '../../define';
import {commandTimeSetting} from './ScheduleItem';
import {dailyScheduleDataInitial} from './ScheduleTemplate';

/**
 * 指定の特定日のスケジュールから当日スケジュールを作成するダイアログ
 * @param props props
 * @returns JSX.Element
 */
const ImportTemplateDialog = (props: {
  /** コピーされるグループのID */
  groupID: string;
  /** ダイアログ表示フラグ */
  isOpenDialog: boolean;
  /** ダイアログ表示フラグ 設定関数 */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** スケジュール更新関数 */
  scheduleChangeHandler: () => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  //prettier-ignore
  //特定日スケジュール
  const [dailySchedule, setDailySchedule] = useState<apiIF.res.getDailySchedule>(dailyScheduleDataInitial);
  //prettier-ignore
  //セレクトボックスで選択した特定日タイプ
  const [selectTemplate, setSelectTemplate] = useState<keyof apiIF.res.getDailySchedule>('weekday-schedule');

  //フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時エラーメッセージ表示フラグ

  //特定日スケジュールの取得
  useEffect(() => {
    if (siteID !== undefined) {
      getDailySchedule(siteID, props.groupID).then(response => {
        setDailySchedule(response);
      });
    }
  }, [siteID, props.groupID]);

  /**
   * セレクトボックスで選択した特定日のスケジュールを
   * 当日スケジュールにコピーするためのBodyデータを作成
   * @param  response 特定日スケジュールの取得データ
   * @returns 修正後のリクエストBodyのデータ
   */
  const copyToScheduleData = (response: apiIF.res.getDailySchedule) => {
    //responseの特定日スケジュールデータをcopyデータの'today-schedule'に上書きする
    const copyData: apiIF.reqParameters.putTodaySchedule = {
      'today-schedule': response[selectTemplate],
    };
    return copyData;
  };

  /**
   * OKボタン押下時処理
   */
  const handleClickButton = () => {
    if (siteID === undefined) return;

    setAPIWait(true);
    const importData = copyToScheduleData(dailySchedule);
    putTodaySchedule(siteID, props.groupID, importData)
      .then(response => {
        //スケジュール設定更新
        props.scheduleChangeHandler();
        handleClose();
      })
      .catch(err => {
        apiErrorAlert(trans);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  /**
   * ダイアログを閉じる処理
   */
  const handleClose = () => {
    setSelectTemplate('holiday-schedule');
    props.setIsOpenDialog(false);
  };

  /**
   * セレクトボックスで選択した特定日のスケジュール表示
   * @param scheduleItemData 画面表示用スケジュールデータ
   * @return JSX.Element
   */
  const SelectTemplateScheduleView = (props: {
    scheduleItemData: apiIF.resParameters.schedule;
  }) => {
    //スケジュールの時間設定が未設定の場合は表示しない
    if (props.scheduleItemData['command-time'] === '--:--') return <></>;
    return (
      <Grid
        container
        alignItems="center"
        className="schedule__importTemplateDialog__content__list__body__row">
        {/** 時刻 */}
        <Grid
          item
          xs={4}
          className="schedule__importTemplateDialog__content__list__body__row__item">
          {commandTimeSetting(props.scheduleItemData['command-time'])}
        </Grid>
        {/** 指令値 */}
        <Grid
          item
          xs={8}
          className="schedule__importTemplateDialog__content__list__body__row__item">
          <ScheduleActionControlContents
            scheduleInfo={props.scheduleItemData}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="import-template-dialog-title"
        maxWidth="md">
        <DialogTitle id="import-template-dialog-title">
          {trans('ImportTemplateDialog.title')}
        </DialogTitle>
        <DialogContent className="schedule__importTemplateDialog__content">
          <Grid container rowSpacing={4}>
            {/** 特定日の種類選択欄 */}
            <Grid item xs={12} container alignItems="flex-end" spacing={3}>
              <Grid
                item
                xs="auto"
                className="schedule__importTemplateDialog__content__template">
                {trans('ImportTemplateDialog.template')}
              </Grid>
              <Grid item xs>
                <TextFieldSelectBox
                  choices={pullDownMenu.SCHEDULE_TEMPLATE}
                  value={selectTemplate}
                  setValue={setSelectTemplate}
                />
              </Grid>
            </Grid>
            {/** 一覧 */}
            <Grid item xs={12}>
              <Stack
                component={Paper}
                className="schedule__importTemplateDialog__content__list">
                {/** ヘッダ */}
                <Box>
                  <Grid
                    container
                    className="schedule__importTemplateDialog__content__list__header">
                    {/** 時刻 */}
                    <Grid
                      item
                      xs={4}
                      className="schedule__importTemplateDialog__content__list__header__item">
                      {trans('ImportTemplateDialog.list.time')}
                    </Grid>
                    {/** 指令値 */}
                    <Grid
                      item
                      xs={8}
                      className="schedule__importTemplateDialog__content__list__header__item">
                      {trans('ImportTemplateDialog.list.action')}
                    </Grid>
                  </Grid>
                </Box>
                {/** ボディ */}
                <Box>
                  <Stack className="schedule__importTemplateDialog__content__list__body">
                    {dailySchedule[selectTemplate].map((schedule, index) => {
                      return (
                        <Box key={`import-schedule${index}`}>
                          <SelectTemplateScheduleView
                            scheduleItemData={schedule}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            onClick={handleClose}
            buttonText={trans('ImportTemplateDialog.buttonTxt.cancel')}
          />
          <PrimaryButton
            onClick={handleClickButton}
            buttonText={trans('ImportTemplateDialog.buttonTxt.apply')}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
      />
    </>
  );
};
export default ImportTemplateDialog;
